// ============================================================================
// ClientDetailPage — route /app/clients/:id  (namespace: "clientDetail")
// ----------------------------------------------------------------------------
// The trainer's TOP-PRIORITY screen: one client's full picture.
//   • ClientHeader   — avatar, name, star toggle, age, key tags, edit + report
//   • Section tabs   — Overview · Measurements · Goals · Sessions (mobile-first;
//     all sections also flow on desktop, the tabs just scroll to them / filter)
//   • Overview       — profile summary (+ availability) and active goals
//   • Measurements   — computed KPI metrics (no fake zeros), trend charts with
//     threshold ref-lines, full history table, and the measurement entry form
//   • Goals          — create / edit / delete with computed progress + ETA
//   • Sessions       — upcoming + past, with schedule entry points
// All data flows through the typed React Query hooks; the AppShell's live-sync
// socket invalidates them so this page updates across devices with no reload.
// Edits/measurements/goals open in an accessible Drawer. Rich, reduced-motion-
// aware motion throughout.
//
// Routing + i18n are pre-wired (see app/router.tsx, i18n/config.ts); this file
// stays a DEFAULT export named ClientDetailPage and only reads :id from the URL.
// ============================================================================
import { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Button, Card, CardHeader, Table, Tabs } from "../../components";
import type { Column } from "../../components";
import { useAccount } from "../../hooks/account";
import { useClient } from "../../hooks/clients";
import { useClientMetrics, useMeasurements } from "../../hooks/measurements";
import { useGoals } from "../../hooks/goals";
import { useRosterUnread } from "../../hooks/messages";
import type { Measurement } from "../../api";
import { LoadingBlock, ErrorBlock } from "../../app/PageState";
import { ClientHeader } from "./ClientHeader";
import { ProfileCard } from "./ProfileCard";
import { ProfileEditForm } from "./ProfileEditForm";
import { MetricsPanel } from "./MetricsPanel";
import { TrendCharts } from "./TrendCharts";
import { MeasurementForm } from "./MeasurementForm";
import { GoalsPanel } from "./GoalsPanel";
import { SessionsPanel } from "./SessionsPanel";
import { PackagePanel } from "./PackagePanel";
import { ClientActivitiesPanel } from "./ClientActivitiesPanel";
import { ClientCalendarPanel } from "./ClientCalendarPanel";
import { ClientNutritionPanel } from "./ClientNutritionPanel";
import { MessagesPanel } from "./MessagesPanel";
import { WeeklyGoalForm } from "./WeeklyGoalForm";
import { PortalPanel } from "./PortalPanel";
import { cn } from "../../lib/cn";
import { fmtDate, fmtNum } from "./lib";

// The detail sections, also the accepted `?tab=` deep-link values (see below).
const SECTION_IDS = [
  "overview",
  "measurements",
  "goals",
  "sessions",
  "nutrition",
  "messages",
] as const;
type SectionId = (typeof SECTION_IDS)[number];

export default function ClientDetailPage() {
  const { t } = useTranslation("clientDetail");
  const reduce = useReducedMotion();
  const params = useParams<{ id: string }>();
  const id = Number(params.id);
  const account = useAccount();
  const validId = Number.isFinite(id) && id >= 0;

  const clientQuery = useClient(validId ? id : undefined);
  const metricsQuery = useClientMetrics(validId ? id : undefined);
  const measurementsQuery = useMeasurements(validId ? id : undefined);
  const goalsQuery = useGoals(validId ? id : undefined, false);
  // One roster-wide unread aggregate drives this client's Messages-tab badge
  // (and is shared with the roster page); kept fresh by the `message` live-sync.
  const unreadQuery = useRosterUnread(validId);

  // Deep-link support: a `?tab=<section>` query opens that section directly (the
  // inactivity "Message" affordance links to .../{id}?tab=messages so a trainer
  // lands straight on the thread to reach out). Seed the initial section
  // SYNCHRONOUSLY from the URL (a lazy initializer, so the first paint is already
  // on the right tab and there's no flash/race on soft navigation), then clear
  // the param in an effect so a later manual tab change isn't fought by the URL.
  const [searchParams, setSearchParams] = useSearchParams();
  const [section, setSection] = useState<SectionId>(() => {
    const tab = searchParams.get("tab");
    return tab && (SECTION_IDS as readonly string[]).includes(tab)
      ? (tab as SectionId)
      : "overview";
  });
  const [editOpen, setEditOpen] = useState(false);
  const [measureOpen, setMeasureOpen] = useState(false);

  useEffect(() => {
    if (!searchParams.has("tab")) return;
    // Apply (covers soft navigation where the component is already mounted for
    // the same route and the lazy initializer doesn't re-run), then strip.
    const tab = searchParams.get("tab");
    if (tab && (SECTION_IDS as readonly string[]).includes(tab)) {
      setSection(tab as SectionId);
    }
    const next = new URLSearchParams(searchParams);
    next.delete("tab");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  if (!validId) {
    return <ErrorBlock />;
  }
  if (clientQuery.isLoading) {
    return <LoadingBlock label={t("loading")} />;
  }
  if (clientQuery.isError || !clientQuery.data) {
    return <ErrorBlock onRetry={() => void clientQuery.refetch()} />;
  }

  const client = clientQuery.data;
  const metrics = metricsQuery.data;
  const goals = goalsQuery.data ?? [];
  const activeGoals = goals.filter((g) => g.status === "active");
  // Unread (client→trainer) messages for THIS client — the Messages-tab badge.
  const unreadMessages = unreadQuery.data?.by_client?.[String(id)] ?? 0;

  const tabs = [
    { id: "overview", label: t("tabs.overview") },
    { id: "measurements", label: t("tabs.measurements") },
    {
      id: "goals",
      label: t("tabs.goals"),
      badge: activeGoals.length > 0 ? <TabCount n={activeGoals.length} /> : undefined,
    },
    { id: "sessions", label: t("tabs.sessions") },
    { id: "nutrition", label: t("nutrition:trainer.tab") },
    {
      id: "messages",
      label: t("messages:tab"),
      badge:
        unreadMessages > 0 ? <TabCount n={unreadMessages} tone="alert" /> : undefined,
    },
  ];

  const fade = (delay = 0) => ({
    initial: reduce ? false : { opacity: 0, y: 14 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.4, delay: reduce ? 0 : delay, ease: [0.22, 0.61, 0.36, 1] as const },
  });

  return (
    <div className="space-y-6">
      {/* Back link */}
      <Link
        to="/app/clients"
        className="inline-flex items-center gap-1.5 text-sm font-bold text-body-soft transition-colors hover:text-coral"
      >
        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" aria-hidden>
          <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        {t("backToClients")}
      </Link>

      <ClientHeader client={client} onEdit={() => setEditOpen(true)} />

      {/* Section tabs */}
      <div className="overflow-x-auto pb-1">
        <Tabs
          value={section}
          onChange={(s) => setSection(s as SectionId)}
          items={tabs}
        />
      </div>

      {/* --- OVERVIEW --- */}
      {section === "overview" && (
        <div className="space-y-6">
          <motion.div {...fade(0)}>
            <ProfileCard client={client} onEdit={() => setEditOpen(true)} />
          </motion.div>
          {metrics && (
            <motion.div {...fade(0.06)}>
              <MetricsPanel metrics={metrics} />
            </motion.div>
          )}
          <motion.div {...fade(0.12)}>
            <GoalsPanel clientId={id} goals={goals} metrics={metrics} />
          </motion.div>
          <motion.div {...fade(0.16)}>
            <PortalPanel client={client} />
          </motion.div>
        </div>
      )}

      {/* --- MEASUREMENTS --- */}
      {section === "measurements" && (
        <div className="space-y-6">
          <motion.div {...fade(0)} className="flex justify-end">
            <Button onClick={() => setMeasureOpen(true)}>
              {t("measure.add")}
            </Button>
          </motion.div>
          {metrics && (
            <motion.div {...fade(0.04)}>
              <MetricsPanel metrics={metrics} />
            </motion.div>
          )}
          {metrics && (
            <motion.div {...fade(0.08)}>
              <TrendCharts metrics={metrics} goals={goals} />
            </motion.div>
          )}
          <motion.div {...fade(0.12)}>
            <MeasurementHistory
              measurements={measurementsQuery.data ?? []}
              loading={measurementsQuery.isLoading}
              onAdd={() => setMeasureOpen(true)}
            />
          </motion.div>
        </div>
      )}

      {/* --- GOALS --- */}
      {section === "goals" && (
        <motion.div {...fade(0)}>
          <GoalsPanel clientId={id} goals={goals} metrics={metrics} />
        </motion.div>
      )}

      {/* --- SESSIONS --- */}
      {section === "sessions" && (
        <div className="space-y-6">
          {/* This client's colour-coded calendar (sessions + homework + goals)
              with a planned-detail of what's upcoming (Wave 8 §C). */}
          <motion.div {...fade(0)}>
            <ClientCalendarPanel clientId={id} />
          </motion.div>
          <motion.div {...fade(0.06)}>
            <SessionsPanel clientId={id} />
          </motion.div>
          <motion.div {...fade(0.08)}>
            <PackagePanel clientId={id} defaultCurrency={account.data?.default_currency ?? null} />
          </motion.div>
          {/* Trainer suggest homework + the client's logged activities. */}
          <motion.div {...fade(0.1)}>
            <ClientActivitiesPanel clientId={id} />
          </motion.div>
        </div>
      )}

      {/* --- NUTRITION --- */}
      {section === "nutrition" && (
        <div className="space-y-6">
          {/* Trainer sets this week's targets + exercise homework; a set goal
              overrides the auto nutrition targets (source flips to 'coach'). */}
          <motion.div {...fade(0)}>
            <WeeklyGoalForm clientId={id} />
          </motion.div>
          <motion.div {...fade(0.06)}>
            <ClientNutritionPanel clientId={id} />
          </motion.div>
        </div>
      )}

      {/* --- MESSAGES --- */}
      {section === "messages" && (
        <motion.div {...fade(0)}>
          <MessagesPanel clientId={id} />
        </motion.div>
      )}

      {/* Report cross-link footer */}
      <Card tone="ink" className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="font-display text-lg font-bold text-ondark">
            {t("report.title")}
          </h3>
          <p className="mt-0.5 text-sm text-ondark-muted">{t("report.blurb")}</p>
        </div>
        <Link to={`/app/clients/${id}/report`}>
          <Button variant="secondary">{t("report.cta")}</Button>
        </Link>
      </Card>

      {/* Drawers */}
      <ProfileEditForm
        client={client}
        open={editOpen}
        onClose={() => setEditOpen(false)}
      />
      <MeasurementForm
        clientId={id}
        open={measureOpen}
        onClose={() => setMeasureOpen(false)}
      />
    </div>
  );
}

function TabCount({ n, tone }: { n: number; tone?: "default" | "alert" }) {
  // 'alert' tints the unread-message badge coral when the tab is inactive (white
  // when the coral tab pill is behind it); 'default' is the neutral count chip.
  return (
    <span
      className={cn(
        "grid h-5 min-w-[1.25rem] place-items-center rounded-pill px-1.5 text-[0.68rem] font-extrabold tabular-nums",
        tone === "alert"
          ? "bg-coral text-white ring-1 ring-white/40"
          : "bg-white/25 text-white",
      )}
    >
      {n}
    </span>
  );
}

// --- measurement history table ---------------------------------------------

function MeasurementHistory({
  measurements,
  loading,
  onAdd,
}: {
  measurements: Measurement[];
  loading: boolean;
  onAdd: () => void;
}) {
  const { t } = useTranslation("clientDetail");

  // Newest first for the table.
  const rows = [...measurements].reverse();

  const cell = (v: number | null, decimals = 1) =>
    v == null ? <span className="text-body-faint">–</span> : fmtNum(v, decimals);

  const columns: Column<Measurement>[] = [
    {
      key: "date",
      header: t("history.date"),
      cell: (r) => (
        <span className="font-bold text-body">{fmtDate(r.taken_at)}</span>
      ),
    },
    { key: "weight", header: t("history.weight"), align: "right", cell: (r) => cell(r.weight_kg) },
    { key: "waist", header: t("history.waist"), align: "right", cell: (r) => cell(r.waist_cm) },
    { key: "chest", header: t("history.chest"), align: "right", cell: (r) => cell(r.chest_cm) },
    { key: "hip", header: t("history.hip"), align: "right", cell: (r) => cell(r.hip_cm) },
    {
      key: "bf",
      header: t("history.bodyFat"),
      align: "right",
      cell: (r) => cell(r.body_fat_pct_manual),
    },
    {
      key: "bp",
      header: t("history.bp"),
      align: "right",
      cell: (r) =>
        r.bp_systolic != null && r.bp_diastolic != null ? (
          `${r.bp_systolic}/${r.bp_diastolic}`
        ) : (
          <span className="text-body-faint">–</span>
        ),
    },
  ];

  return (
    <Card>
      <CardHeader
        title={t("history.title")}
        subtitle={t("history.subtitle")}
      />
      {loading ? (
        <LoadingBlock />
      ) : rows.length === 0 ? (
        <div className="rounded-md bg-surface-sunken px-4 py-8 text-center">
          <p className="text-sm text-body-soft">{t("history.empty")}</p>
          <Button size="sm" className="mt-3" onClick={onAdd}>
            {t("measure.add")}
          </Button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table
            columns={columns}
            rows={rows}
            rowKey={(r) => r.id}
            caption={t("history.title")}
          />
        </div>
      )}
    </Card>
  );
}
